// TGCLOUD-1818 — the popover that builds or edits one criterion.
//
// Small and transient on purpose. The control it replaces was a panel you
// opened, hunted through and closed again; the whole point of chips is that
// the state is on screen at rest, so the editor must get out of the way the
// moment it has an answer. It closes on commit, on Escape and on click-away.
//
// MUI <Popover> rather than <Popper>: Popover brings the modal focus trap and
// the restore-focus-to-anchor behaviour with it. Doing that by hand on a Popper
// is how "Escape left focus on <body>" bugs get written.
//
// It owns ONE thing — the draft criterion — and emits it on commit.

import React from 'react';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import FilterValueInput from './FilterValueInput';
import { OPERATOR_LABEL_KEYS } from './filterLabels';
import { isRange, isValueless, toFilterDate } from './filterPredicates';

const POPOVER_WIDTH = 330;

/** A criterion is committable once its operator has the value it needs. */
export const isCommittable = (draft) => {
  if (!draft?.field || !draft?.operator) return false;
  if (isValueless(draft.operator)) return true;
  if (isRange(draft.operator))
    return Boolean(draft.value?.from && draft.value?.to);
  return String(draft.value ?? '').trim().length > 0;
};

/**
 * @param {object} props
 * @param {HTMLElement|null} props.anchorEl
 * @param {boolean} props.open
 * @param {() => void} props.onClose             called for cancel, Escape and click-away
 * @param {(criterion: object) => void} props.onCommit
 * @param {Array} props.fields                   served descriptors
 * @param {object|null} props.initial            criterion being edited, or null for a new one
 * @param {(field: object) => Array} [props.optionsFor] enum choices for a field
 * @param {(field: object) => string} props.labelFor
 * @param {(s:string)=>string} props.t
 */
export default function FilterBuilder({
  anchorEl,
  open,
  onClose,
  onCommit,
  fields,
  initial,
  optionsFor,
  labelFor,
  t
}) {
  const isEdit = Boolean(initial);
  const valueRef = React.useRef(null);

  const [draft, setDraft] = React.useState(() => initial || null);

  // Re-seed whenever the popover opens. Keyed on `open` rather than on
  // `initial` so that reopening on the SAME chip still resets a half-edited
  // draft the user abandoned with Escape last time.
  React.useEffect(() => {
    if (!open) return;
    setDraft(
      initial || {
        field: fields?.[0]?.key ?? '',
        operator: fields?.[0]?.operators?.[0] ?? '',
        value: ''
      }
    );
  }, [open, initial, fields]);

  const field = React.useMemo(
    () => (fields || []).find((f) => f.key === draft?.field) || null,
    [fields, draft?.field]
  );

  const onFieldChange = (key) => {
    const next = (fields || []).find((f) => f.key === key);
    setDraft({
      field: key,
      // The operator list repopulates from THIS field, and the previous
      // operator is kept only if the new field actually allows it. Keeping it
      // blindly is how a UI ends up offering `contains` on a field whose
      // allowlist has no such entry.
      operator: next?.operators?.includes(draft?.operator)
        ? draft.operator
        : next?.operators?.[0] ?? '',
      value: ''
    });
  };

  const onOperatorChange = (operator) => {
    setDraft((current) => ({
      ...current,
      operator,
      // A range and a scalar cannot share a value shape, and neither can carry
      // over to isEmpty. Reset rather than coerce.
      value: isRange(operator) ? { from: '', to: '' } : ''
    }));
  };

  // Canonicalise before it leaves the builder.
  //
  // DateInput publishes a local `Date` object. Held in state that works, because
  // toFilterDate reads a Date by its local components — but the criterion is
  // ALSO serialised into the URL, and there `String(new Date(...))` becomes
  // "Mon Mar 12 1965 00:00:00 GMT+0000 (…)". Parsed back, that string fails
  // toFilterDate's ISO test, the predicate collapses to `() => false`, and a
  // shared link or a plain browser reload silently returns ZERO people while
  // still showing the chip. Normalising here means only one shape ever exists.
  const commit = () => {
    if (!isCommittable(draft)) return;
    if (field?.type === 'date') {
      const value = isRange(draft.operator)
        ? {
            from: toFilterDate(draft.value?.from),
            to: toFilterDate(draft.value?.to)
          }
        : draft.operator === 'inYear'
        ? String(draft.value ?? '')
        : toFilterDate(draft.value);
      onCommit({ ...draft, value });
      return;
    }
    onCommit(draft);
  };

  const operators = field?.operators || [];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      // Flip above the anchor when there is no room below, so the popover
      // never has to overlap the grid header to stay on screen.
      slotProps={{
        paper: {
          sx: { width: POPOVER_WIDTH, p: 2, overflow: 'visible' }
        }
      }}
      // Popover restores focus to the anchor on close by default — on commit,
      // on Escape and on click-away alike. That is the behaviour the brief
      // asks for, and it is why this is a Popover and not a Popper.
      disableRestoreFocus={false}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            select
            label={t('Field')}
            value={draft?.field ?? ''}
            onChange={(e) => onFieldChange(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          >
            {(fields || []).map((f) => (
              <MenuItem key={f.key} value={f.key}>
                {labelFor(f)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('Operator')}
            value={draft?.operator ?? ''}
            onChange={(e) => onOperatorChange(e.target.value)}
            size="small"
            fullWidth
            // A single-operator field still renders the select, disabled, so
            // the chip always reads as a full sentence and the user can see
            // WHY there is no choice.
            disabled={operators.length <= 1}
          >
            {operators.map((op) => (
              <MenuItem key={op} value={op}>
                {t(OPERATOR_LABEL_KEYS[op] || op)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <FilterValueInput
          field={field}
          operator={draft?.operator}
          value={draft?.value}
          onChange={(value) => setDraft((current) => ({ ...current, value }))}
          onCommit={commit}
          options={optionsFor?.(field)}
          t={t}
          inputRef={valueRef}
        />

        {field?.encrypted ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t(
              'Identifiers and names are matched exactly by default. This is the case where "contains" gives you false hits.'
            )}
          </Typography>
        ) : null}

        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={commit}
            disabled={!isCommittable(draft)}
          >
            {isEdit ? t('Update') : t('Add filter')}
          </Button>
          <Button size="small" onClick={onClose}>
            {t('Cancel')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
